import classNames from 'classnames';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import usePrevious from '../../../utils/use-previous';
import './popover.scss';

interface Props {
	open: boolean;
	onClose?: () => void;
	children: ReactNode;
	style?: React.CSSProperties;
	bottom?: boolean;
}

export function Popover({
	open,
	onClose,
	children,
	style = {},
	bottom = false
}: Props): JSX.Element {
	const selector = useRef<HTMLDivElement | null>(null);
	const [displayNone, setDisplayNone] = useState(true);

	const prevOpen = usePrevious(open);

	const timeout = useRef<NodeJS.Timeout | null>(null);
	useEffect(() => {
		// We want to set the content to display none if it's closed
		// otherwise everything inside can be tabbed to and clicked when it's collapsed.
		// But we also want to maintain the css transitions, so we need to delay setting it to display none
		// until it's collapsed
		if (timeout.current) {
			clearTimeout(timeout.current);
		}
		if (open) {
			setDisplayNone(false);
		} else {
			timeout.current = setTimeout(() => {
				setDisplayNone(true);
			}, 200);
		}
	}, [open]);

	// on escape, close dropdown
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose && onClose();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [onClose]);

	useEffect(() => {
		function checkClick(e: any) {
			// looks this css key not need more if (e.target?.className?.includes?.('popover-opener')) return;
			const path = e.path || e.composedPath();
			for (const item of path) {
				if (selector.current === item) return;
			}
			onClose && onClose();
		}
		// we need to check if it's already open, otherwise it immediately closes
		if (prevOpen && open) {
			window.addEventListener('click', checkClick);
		}
		return () => window.removeEventListener('click', checkClick);
	}, [open, onClose, prevOpen]);

	return (
		<div
			className={classNames('popover-wrapper', { open, bottom })}
			style={style}
			ref={selector}
		>
			<div style={{ display: displayNone ? 'none' : 'block' }}>
				{children}
			</div>
		</div>
	);
}
